import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1C1917] text-white" data-testid="footer">
      <div className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <div className="flex items-center gap-3">
                <img 
                  src="/logo_final.png" 
                  alt="Marapyan CPA Logo" 
                  className="w-9 h-9 sm:w-11 sm:h-11 object-contain shrink-0"
                />
                <div className="whitespace-nowrap">
                  <span
                    className="uppercase text-sm sm:text-base md:text-lg text-white leading-none tracking-[0.03em]"
                    style={{ fontFamily: "'Cinzel Decorative', 'Cormorant Garamond', serif", fontWeight: 400 }}
                  >
                    MARAPYAN CPA, INC
                  </span>
                </div>
              </div>
            </Link>
            <p className="text-[#A8A29E] text-sm leading-relaxed mb-6">
              Your trusted partner in financial clarity. A virtual CPA firm
              providing tax, accounting, and bookkeeping services to individuals
              and businesses in Burbank, Los Angeles, and across California.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-medium mb-6 text-white">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: "Home", href: "/" },
                { name: "Services & Industries", href: "/#services" },
                { name: "About Us", href: "/about" },
                { name: "Our Process", href: "/#process" },
                { name: "Client Access", href: "https://marapyancpa.securefilepro.com/portal/#/login", external: true },
              ].map((link) => (
                <li key={link.name}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#A8A29E] hover:text-[#C85A43] transition-colors text-sm"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-[#A8A29E] hover:text-[#C85A43] transition-colors text-sm"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-lg font-medium mb-6 text-white">Services</h4>
            <ul className="space-y-3">
              {[
                "Tax Preparation",
                "Tax Planning",
                "Bookkeeping",
                "Accounting",
                "Business Consulting",
              ].map((service) => (
                <li key={service}>
                  <span className="text-[#A8A29E] text-sm">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-medium mb-6 text-white">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#C85A43] flex-shrink-0 mt-0.5" />
                <span className="text-[#A8A29E] text-sm">
                  221 N Third St, #393<br />
                  Burbank, CA 91502
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#C85A43] flex-shrink-0" />
                <a 
                  href="tel:+18186051215" 
                  className="text-[#A8A29E] hover:text-[#C85A43] transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  (818) 605-1215
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#C85A43] flex-shrink-0" />
                <a 
                  href="mailto:info@marapyancpa.com" 
                  className="text-[#A8A29E] hover:text-[#C85A43] transition-colors text-sm"
                  data-testid="footer-email"
                >
                  info@marapyancpa.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#C85A43] flex-shrink-0 mt-0.5" />
                <span className="text-[#A8A29E] text-sm">
                  Mon - Fri: 9:00 AM - 6:00 PM PT<br />
                  Sat: By Appointment
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#292524] mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#78716C] text-sm">
            &copy; {currentYear} Marapyan CPA, Inc. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link 
              to="/privacy" 
              className="text-[#78716C] hover:text-[#C85A43] transition-colors text-sm"
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              className="text-[#78716C] hover:text-[#C85A43] transition-colors text-sm"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
